import { toast } from 'sonner';
import type { TaskLabel, TaskLabelColor } from '@shared/types/tasks';
import { createTaskLabel, deleteTaskLabel, updateTaskLabel } from '@/lib/tasks-api';
import { optimisticLabel } from '@/stores/taskOptimistic';

function nameTaken(labels: TaskLabel[], name: string, exceptId?: string): boolean {
  return labels.some((l) => l.id !== exceptId && l.name.trim().toLowerCase() === name.trim().toLowerCase());
}

/**
 * Create a label instantly (the id is chosen here, so it can be picked straight away).
 * Returns the new label, or null if the name is already used.
 */
export function createLabelNow(labels: TaskLabel[], name: string, color: TaskLabelColor): TaskLabel | null {
  const clean = name.trim();
  if (!clean) return null;
  if (nameTaken(labels, clean)) {
    toast.error('There is already a label with that name');
    return null;
  }
  const label: TaskLabel = { id: crypto.randomUUID(), name: clean, color, position: labels.length };
  void optimisticLabel({ id: label.id, create: label }, createTaskLabel({ id: label.id, name: clean, color }));
  return label;
}

export function updateLabelNow(labels: TaskLabel[], id: string, fields: { name?: string; color?: TaskLabelColor }): void {
  if (fields.name !== undefined && nameTaken(labels, fields.name, id)) {
    toast.error('There is already a label with that name');
    return;
  }
  void optimisticLabel({ id, fields }, updateTaskLabel(id, fields));
}

export function deleteLabelNow(id: string): void {
  void optimisticLabel({ id, deleted: true }, deleteTaskLabel(id));
}
